import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import { App } from 'antd'

interface User {
  id: string | number
  username: string
  email?: string
  first_name?: string
  last_name?: string
  role?: string
  permissions?: string[]
  is_superuser?: boolean
  is_staff?: boolean
  profile_role?: string  // 커스텀 역할 (super_admin, admin, manager, staff, viewer)
}

interface MenuPermissions {
  [menuKey: string]: boolean
}

interface AuthContextType {
  user: User | null
  token: string | null
  isAuthenticated: boolean
  isLoading: boolean
  menuPermissions: MenuPermissions
  hasMenuPermission: (menuKey: string) => boolean
  login: (username: string, password: string) => Promise<boolean>
  logout: () => void
  // 마지막 로그인 실패 사유 (로그인 폼에 인라인 표시용). 성공 시 null.
  lastError: string | null
}

const AuthContext = createContext<AuthContextType | null>(null)

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

// AuthProvider 외부에서 한 번만 실행되는 초기화 함수
const getInitialAuth = () => {
  try {
    // token과 auth_token 둘 다 확인 (하위 호환성)
    const storedAuthToken = localStorage.getItem('auth_token')
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')


    let initialUser: User | null = null
    let initialToken: string | null = null

    // auth_token 우선, 없으면 token 확인 (하위 호환성)
    const tokenToUse = storedAuthToken || storedToken
    if (tokenToUse && tokenToUse !== 'null' && tokenToUse !== 'undefined') {
      initialToken = tokenToUse

      // 토큰이 auth_token에 없고 token에만 있으면 auth_token으로 복사
      if (!storedAuthToken && storedToken) {
        localStorage.setItem('auth_token', storedToken)
      }
    }

    if (storedUser && storedUser !== 'null' && storedUser !== 'undefined') {
      try {
        const parsedUser = JSON.parse(storedUser)
        if (parsedUser && parsedUser.id && parsedUser.username) {
          initialUser = parsedUser
        }
      } catch (e) {
        console.error('[AuthProvider] Failed to parse stored user')
        localStorage.removeItem('user')
      }
    }

    // User는 있는데 Token이 없는 경우 (불일치 상태) - 모두 제거
    if (initialUser && !initialToken) {
      console.warn('[AuthProvider] User exists but token missing - clearing auth state')
      localStorage.removeItem('user')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      return { initialUser: null, initialToken: null }
    }

    // Token은 있는데 User가 없는 경우 (불일치 상태) - 모두 제거
    if (initialToken && !initialUser) {
      console.warn('[AuthProvider] Token exists but user missing - clearing auth state')
      localStorage.removeItem('user')
      localStorage.removeItem('auth_token')
      localStorage.removeItem('token')
      localStorage.removeItem('username')
      return { initialUser: null, initialToken: null }
    }

    return { initialUser, initialToken }
  } catch (error) {
    console.error('[AuthProvider] Error reading initial auth:', error)
    return { initialUser: null, initialToken: null }
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const navigate = useNavigate()

  // useState의 lazy initialization을 사용하여 한 번만 실행
  const [authState] = useState(() => {
    const { initialUser, initialToken } = getInitialAuth()
    return { initialUser, initialToken }
  })

  // antd v5: 정적 message 대신 App 컨텍스트의 message 사용 (커스텀 테마/홀더에 정상 렌더)
  const { message } = App.useApp()
  const [lastError, setLastError] = useState<string | null>(null)

  const [user, setUser] = useState<User | null>(authState.initialUser)
  const [token, setToken] = useState<string | null>(authState.initialToken)
  // 저장된 인증 정보가 있으면 검증이 필요하므로 isLoading을 true로 시작
  const [isLoading, setIsLoading] = useState(!!authState.initialToken)
  const [menuPermissions, setMenuPermissions] = useState<MenuPermissions>({})


  // 메뉴 권한 로드 함수
  const loadMenuPermissions = async (authToken: string) => {
    try {
      const response = await fetch('/api/users/permissions/', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        const data = await response.json()
        if (data.success && data.permissions) {
          // permissions 객체에서 can_view가 true인 메뉴만 추출
          const menuPerms: MenuPermissions = {}
          for (const [key, value] of Object.entries(data.permissions)) {
            const perm = value as { view?: boolean }
            menuPerms[key] = perm.view === true
          }
          setMenuPermissions(menuPerms)
        }
      }
    } catch (error) {
      console.error('[AuthProvider] Failed to load menu permissions:', error)
    }
  }

  // 토큰 유효성 검증 (초기 로드 시)
  useEffect(() => {
    const validateToken = async () => {
      if (!authState.initialToken) {
          setIsLoading(false)
        return
      }


      try {
        const response = await fetch('/api/auth/verify/', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${authState.initialToken}`,
            'Content-Type': 'application/json',
          },
        })

        const data = await response.json()

        if (response.ok && data.valid) {
          // 서버에서 최신 사용자 정보로 업데이트
          const userData: User = {
            id: data.user.id,
            username: data.user.username,
            email: data.user.email,
            first_name: data.user.first_name || '',
            last_name: data.user.last_name || '',
            role: data.user.is_superuser ? 'admin' : 'user',
            permissions: data.user.is_staff ? ['read', 'write'] : ['read'],
            is_superuser: data.user.is_superuser,
            is_staff: data.user.is_staff,
            profile_role: data.user.profile_role
          }
          setUser(userData)
          localStorage.setItem('user', JSON.stringify(userData))

          // 메뉴 권한 로드
          await loadMenuPermissions(authState.initialToken)
        } else {
          // 토큰이 유효하지 않으면 로컬 스토리지 및 상태 초기화
          setUser(null)
          setToken(null)
          setMenuPermissions({})
          localStorage.removeItem('auth_token')
          localStorage.removeItem('token')
          localStorage.removeItem('user')
          localStorage.removeItem('username')
          document.cookie = 'jwt_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
        }
      } catch (error) {
        console.error('[AuthProvider] Token validation failed:', error)
        // 네트워크 오류 시에도 인증 상태 초기화
        setUser(null)
        setToken(null)
        setMenuPermissions({})
        localStorage.removeItem('auth_token')
        localStorage.removeItem('token')
        localStorage.removeItem('user')
        localStorage.removeItem('username')
        document.cookie = 'jwt_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'
      } finally {
        setIsLoading(false)
      }
    }

    validateToken()
  }, []) // 초기 마운트 시 한 번만 실행

  const login = async (username: string, password: string): Promise<boolean> => {
    setLastError(null)
    try {
      const response = await fetch('/api/auth/login/', {
        method: 'POST',
        credentials: 'include',  // sessionid 쿠키 저장 (analyze 미들웨어 호환)
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password })
      })

      const text = await response.text()
      if (!text) {
        const m = '서버 응답이 비어있습니다.'
        setLastError(m); message.error(m)
        return false
      }

      let data
      try {
        data = JSON.parse(text)
      } catch (e) {
        const m = '서버 응답 형식이 잘못되었습니다.'
        setLastError(m); message.error(m)
        return false
      }

      if (response.ok && data.success) {
        const userData: User = {
          id: data.user.id,
          username: data.user.username,
          email: data.user.email,
          first_name: data.user.first_name || '',
          last_name: data.user.last_name || '',
          role: data.user.is_superuser ? 'admin' : 'user',
          permissions: data.user.is_staff ? ['read', 'write'] : ['read'],
          is_superuser: data.user.is_superuser,
          is_staff: data.user.is_staff,
          profile_role: data.user.profile_role
        }

        // 상태 업데이트
        setToken(data.token)
        setUser(userData)

        // localStorage에 저장 (token과 auth_token 둘 다 저장 - 하위 호환성)
        localStorage.setItem('auth_token', data.token)
        localStorage.setItem('token', data.token)  // 하위 호환성을 위해 추가
        localStorage.setItem('user', JSON.stringify(userData))
        localStorage.setItem('username', userData.username)

        // refresh_token 저장 (토큰 자동 갱신용)
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token)
        }

        // 쿠키에도 JWT 토큰 저장 (Django 피킹 페이지에서 사용)
        document.cookie = `jwt_token=${data.token}; path=/; max-age=86400; SameSite=Strict; Secure`

        // 메뉴 권한 로드
        await loadMenuPermissions(data.token)

        setLastError(null)
        message.success('로그인 성공!')

        return true
      } else {
        const m = data.error || '아이디 또는 비밀번호가 올바르지 않습니다.'
        setLastError(m); message.error(m)
        return false
      }
    } catch (error) {
      console.error('[AuthProvider] Login failed:', error)
      const m = '네트워크 오류가 발생했습니다.'
      setLastError(m); message.error(m)
      return false
    }
  }

  const logout = () => {
    // 상태 초기화
    setUser(null)
    setToken(null)
    setMenuPermissions({})

    // localStorage 초기화 (token과 auth_token 둘 다 제거)
    localStorage.removeItem('auth_token')
    localStorage.removeItem('token')  // 하위 호환성을 위해 추가
    localStorage.removeItem('user')
    localStorage.removeItem('username')

    // 쿠키 삭제
    document.cookie = 'jwt_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT'

    // 로그인 페이지로 이동
    navigate('/login')
  }

  // 메뉴 권한 체크 함수
  const hasMenuPermission = (menuKey: string): boolean => {
    // 슈퍼유저는 모든 권한
    if (user?.is_superuser) return true
    // 권한이 없으면 false
    return menuPermissions[menuKey] === true
  }

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    menuPermissions,
    hasMenuPermission,
    login,
    logout,
    lastError
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}